import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LoginModal } from '../components/LoginModal';
import { useAuth } from '../hooks/useAuth';
import {
  fetchChoirContent,
  saveChoirContent,
  subscribeToChoir,
  unsubscribeFromChoir,
  type ChoirContent,
} from '../services/api';
import { formatPhone } from '../utils/phone';

const EMPTY_CONTENT: ChoirContent = { notes: '', info: '', schedule: '', fileLink: '', fileDownloadable: false };

export function ChoirPage() {
  const { isChoirAuthenticated, loginForChoir } = useAuth();

  const [content, setContent] = useState<ChoirContent>(EMPTY_CONTENT);
  const [editDraft, setEditDraft] = useState<ChoirContent>(EMPTY_CONTENT);
  const [isEditing, setIsEditing] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [loading, setLoading] = useState(true);

  // Sign-up form state
  const [signUpFirstName, setSignUpFirstName] = useState('');
  const [signUpLastName, setSignUpLastName] = useState('');
  const [signUpPhone, setSignUpPhone] = useState('');
  const [signUpStatus, setSignUpStatus] = useState('');
  const [signUpSubmitting, setSignUpSubmitting] = useState(false);

  // Remove form state
  const [removePhone, setRemovePhone] = useState('');
  const [removeLastName, setRemoveLastName] = useState('');
  const [removeStatus, setRemoveStatus] = useState('');
  const [removeSubmitting, setRemoveSubmitting] = useState(false);

  useEffect(() => {
    fetchChoirContent()
      .then((data) => {
        setContent(data);
        setEditDraft(data);
      })
      .catch(() => {
        setContent(EMPTY_CONTENT);
        setEditDraft(EMPTY_CONTENT);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleEditClick = useCallback(() => {
    if (isChoirAuthenticated) {
      setEditDraft({ ...content });
      setIsEditing(true);
    } else {
      setShowLoginModal(true);
    }
  }, [isChoirAuthenticated, content]);

  const handleLoginSuccess = useCallback(
    async (password: string) => {
      const success = await loginForChoir(password);
      if (success) {
        setEditDraft({ ...content });
        setIsEditing(true);
      }
      return success;
    },
    [loginForChoir, content]
  );

  const handleLoginAndSave = useCallback(
    async (password: string) => {
      const result = await saveChoirContent(editDraft, password);
      if (result.success) {
        setContent({ ...editDraft });
        setIsEditing(false);
        setSaveStatus('Saved!');
        setTimeout(() => setSaveStatus(''), 3000);
        return true;
      }
      return false;
    },
    [editDraft]
  );

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditDraft({ ...content });
  }, [content]);

  const handleSignUp = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSignUpSubmitting(true);
      setSignUpStatus('');
      const result = await subscribeToChoir(signUpFirstName, signUpLastName, signUpPhone);
      setSignUpSubmitting(false);
      if (result.success) {
        setSignUpStatus('You have been added to the choir text list!');
        setSignUpFirstName('');
        setSignUpLastName('');
        setSignUpPhone('');
      } else {
        setSignUpStatus(result.error || 'Something went wrong. Please try again.');
      }
    },
    [signUpFirstName, signUpLastName, signUpPhone]
  );

  const handleRemove = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setRemoveSubmitting(true);
      setRemoveStatus('');
      const result = await unsubscribeFromChoir(removePhone, removeLastName);
      setRemoveSubmitting(false);
      if (result.success) {
        setRemoveStatus('You have been removed from the choir text list.');
        setRemovePhone('');
        setRemoveLastName('');
      } else {
        setRemoveStatus(result.error || 'No matching subscriber found. Check your phone number and last name.');
      }
    },
    [removePhone, removeLastName]
  );

  // When editing and already authenticated, save directly (login modal is for new sessions)
  const handleSaveDirect = useCallback(async () => {
    // When already authenticated, we still need a password for the POST body.
    // Show modal to re-enter password for the save operation.
    setShowLoginModal(true);
  }, []);

  if (loading) {
    return (
      <div className="page-container">
        <Link to="/" className="home-page__back-link">← Percy Priest Branch</Link>
        <h1>Choir</h1>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={isEditing ? handleLoginAndSave : handleLoginSuccess}
        title="Enter Password"
        description="Enter the password to edit choir content."
      />

      <div className="page-container">
        <div className="choir-page__top-nav">
          <Link to="/" className="home-page__back-link">← Percy Priest Branch</Link>
          <Link to="/choir/admin" className="choir-page__admin-link">Admin</Link>
        </div>

        <div className="page-header">
          <h1>Choir</h1>
          {!isEditing && (
            <button className="btn-secondary" onClick={handleEditClick}>
              Edit Content
            </button>
          )}
          {saveStatus && <span className="choir-page__save-status">{saveStatus}</span>}
        </div>

        {/* Content Section */}
        <div className="choir-page__section">
          {isEditing ? (
            <div className="choir-page__edit-form">
              <div className="choir-page__field">
                <label className="choir-page__label">Notes</label>
                <textarea
                  className="choir-page__textarea"
                  value={editDraft.notes}
                  onChange={(e) => setEditDraft((d) => ({ ...d, notes: e.target.value }))}
                  rows={4}
                  placeholder="General notes..."
                />
              </div>
              <div className="choir-page__field">
                <label className="choir-page__label">Info</label>
                <textarea
                  className="choir-page__textarea"
                  value={editDraft.info}
                  onChange={(e) => setEditDraft((d) => ({ ...d, info: e.target.value }))}
                  rows={4}
                  placeholder="Choir information..."
                />
              </div>
              <div className="choir-page__field">
                <label className="choir-page__label">Schedule</label>
                <textarea
                  className="choir-page__textarea"
                  value={editDraft.schedule}
                  onChange={(e) => setEditDraft((d) => ({ ...d, schedule: e.target.value }))}
                  rows={4}
                  placeholder="Upcoming schedule..."
                />
              </div>
              <div className="choir-page__field">
                <label className="choir-page__label">File / Resource Link</label>
                <input
                  type="url"
                  className="choir-page__input"
                  value={editDraft.fileLink}
                  onChange={(e) => setEditDraft((d) => ({ ...d, fileLink: e.target.value }))}
                  placeholder="https://example.com/choir-music.pdf"
                />
                <label className="choir-page__checkbox-label">
                  <input
                    type="checkbox"
                    checked={editDraft.fileDownloadable}
                    onChange={(e) => setEditDraft((d) => ({ ...d, fileDownloadable: e.target.checked }))}
                  />
                  Show download button
                </label>
              </div>
              <div className="choir-page__edit-actions">
                <button className="btn-secondary" onClick={handleCancelEdit}>
                  Cancel
                </button>
                <button className="btn-success" onClick={handleSaveDirect}>
                  Save Changes
                </button>
              </div>
            </div>
          ) : (
            <div className="choir-page__content">
              {content.notes && (
                <div className="choir-page__block">
                  <h2>Notes</h2>
                  <p className="choir-page__text">{content.notes}</p>
                </div>
              )}
              {content.info && (
                <div className="choir-page__block">
                  <h2>Info</h2>
                  <p className="choir-page__text">{content.info}</p>
                </div>
              )}
              {content.schedule && (
                <div className="choir-page__block">
                  <h2>Schedule</h2>
                  <p className="choir-page__text">{content.schedule}</p>
                </div>
              )}
              {content.fileLink && (
                <div className="choir-page__block">
                  <h2>Resources</h2>
                  <div className="choir-page__file-actions">
                    <a
                      href={content.fileLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="choir-page__file-link"
                    >
                      View File / Resource
                    </a>
                    {content.fileDownloadable && (
                      <a
                        href={`/api/choir/download?url=${encodeURIComponent(content.fileLink)}`}
                        className="choir-page__file-link choir-page__file-link--download"
                      >
                        Download
                      </a>
                    )}
                  </div>
                </div>
              )}
              {!content.notes && !content.info && !content.schedule && !content.fileLink && (
                <p className="choir-page__empty">No content yet. Click "Edit Content" to add information.</p>
              )}
            </div>
          )}
        </div>

        {/* Sign Up Section */}
        <div className="choir-page__section">
          <h2>Sign Up for Text Messages</h2>
          <p className="choir-page__section-desc">
            Enter your information below to receive choir text messages.
          </p>
          <form className="choir-page__form" onSubmit={handleSignUp}>
            <div className="choir-page__form-row">
              <input
                type="text"
                className="choir-page__input"
                placeholder="First Name"
                value={signUpFirstName}
                onChange={(e) => setSignUpFirstName(e.target.value)}
                required
              />
              <input
                type="text"
                className="choir-page__input"
                placeholder="Last Name"
                value={signUpLastName}
                onChange={(e) => setSignUpLastName(e.target.value)}
                required
              />
            </div>
            <input
              type="tel"
              className="choir-page__input"
              placeholder="(555) 555-5555"
              value={signUpPhone}
              onChange={(e) => setSignUpPhone(formatPhone(e.target.value))}
              required
            />
            <button type="submit" className="btn-primary" disabled={signUpSubmitting}>
              {signUpSubmitting ? 'Signing up...' : 'Sign Up'}
            </button>
            {signUpStatus && (
              <p className={`choir-page__status ${signUpStatus.startsWith('You have been added') ? 'choir-page__status--success' : 'choir-page__status--error'}`}>
                {signUpStatus}
              </p>
            )}
          </form>
        </div>

        {/* Remove Section */}
        <div className="choir-page__section">
          <h2>Remove Yourself from the List</h2>
          <p className="choir-page__section-desc">
            Enter your cell phone number and last name to be removed.
          </p>
          <form className="choir-page__form" onSubmit={handleRemove}>
            <input
              type="tel"
              className="choir-page__input"
              placeholder="(555) 555-5555"
              value={removePhone}
              onChange={(e) => setRemovePhone(formatPhone(e.target.value))}
              required
            />
            <input
              type="text"
              className="choir-page__input"
              placeholder="Last Name"
              value={removeLastName}
              onChange={(e) => setRemoveLastName(e.target.value)}
              required
            />
            <button type="submit" className="btn-danger" disabled={removeSubmitting}>
              {removeSubmitting ? 'Removing...' : 'Remove Me'}
            </button>
            {removeStatus && (
              <p className={`choir-page__status ${removeStatus.startsWith('You have been removed') ? 'choir-page__status--success' : 'choir-page__status--error'}`}>
                {removeStatus}
              </p>
            )}
          </form>
        </div>
      </div>
    </>
  );
}
